import React, { useCallback, useContext, useMemo } from 'react';
import { EDAutoCompleteEdit } from '../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import EDCaptionedValue from '../../../../../../../BaseComponents/CaptionedValue/EDCaptionedValue';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import DisplayText from '../../../../../../../Components/Attributes/DisplayText/DisplayText';
import { KodplanContext } from '../../../../../../../Components/KodplanManager/Context/KodplanContext';
import {
  TEDEKBokforderSTATUSEnum,
  TEDEKBokforderTYPEnum,
} from '../../../../../../../globalQueryTypes';
import { ModelContext } from '../../../../../../../Model/ModelContext/ModelContext';
import { TEDFormatDefault } from '../../../../../../../Utils/Formatters/FormatterClasses/TEDFormatDefault';
import { useBusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { FormUpdateAssociatonType } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import KonteringContext from '../../../../../_Common/Components/KonteringManager/KonteringContext';
import { IKodstrang } from '../../../../../_Common/Components/KonteringManager/KonteringManager';
import { BokforderWrapperContext } from '../../../../Rutiner/Bokforder/Contexts/BokforderWrapperContext';
import { createKodstrang } from '../../Hooks/useValidateBokforder';
import { UserBokforderKodbehorigheterQuery_userBokforderKodbehorigheter } from '../../Queries/BokforderKodbehorigheter/_QueryTypes/UserBokforderKodbehorigheterQuery';
import { UtanordningType__Associations_Rader } from '../../Queries/_QueryTypes/UtanordningType';
import { createUsersWithKodbehBasedOnType } from '../../Utils/BfoUtanordningUtils';
import {
  findFranvaroForUser,
  getKodplanForBokfOrder,
  getReservNamesBasedOnFranvaroList,
} from '../../Utils/BokforderUtils';
import BokforderContext from '../BokforderContext/BokforderContext';
import { IUserBokforderKodbehorigheter } from '../BokforderContext/BokforderContextManager';
import { FranvaroContext } from '../../../../../SystemAdministration/ObjektVyer/User/Context/FranvaroContext/FranvaroContext';
import { UtanordningQuery_node_TEDEKBokforder } from '../../Queries/_QueryTypes/UtanordningQuery';
import { filterValidKontrollattestantOption } from '../../Utils/UtanordningUtils';

interface IProps {
  isDisabled?: boolean;
}

const KontrollattestantPicker: React.FC<IProps> = props => {
  const bokforderContext = useContext(BokforderContext);
  const bokforderWrapperContext = useContext(BokforderWrapperContext);
  const formContext =
    useBusinessDataFormContext<UtanordningQuery_node_TEDEKBokforder>();
  const franvaroContext = useContext(FranvaroContext);
  const { userID } = useContext(ActiveDatabaseContext);
  const { avenyCollectionMap } = useContext(ModelContext);
  const konteringContext = useContext(KonteringContext);
  const kodplaner = useContext(KodplanContext);
  const formContextUpdate = formContext?.update;

  const bokforderDate: Date = useMemo(
    () =>
      formContext?.formState?.currentValues?.BOKF_DATUM
        ? new Date(formContext?.formState?.currentValues?.BOKF_DATUM)
        : new Date(),
    [formContext?.formState?.currentValues?.BOKF_DATUM]
  );

  const allKontrollattestanter: IUserBokforderKodbehorigheter[] = useMemo(
    () =>
      createUsersWithKodbehBasedOnType(
        bokforderWrapperContext.allBFOUsers,
        avenyCollectionMap,
        bokforderDate,
        'UTANORDNING_KONTROLL',
        userID ?? '',
        bokforderWrapperContext.isEconomyPersonnel
      ),
    [
      avenyCollectionMap,
      bokforderDate,
      bokforderWrapperContext.allBFOUsers,
      bokforderWrapperContext.isEconomyPersonnel,
      userID,
    ]
  );
  const bokforderKodplan = useMemo(
    () => getKodplanForBokfOrder(kodplaner.availableKodplanList, bokforderDate),
    [bokforderDate, kodplaner.availableKodplanList]
  );

  const enhancedBokforderKodplan = useMemo(
    () =>
      konteringContext.konteringUtils.getKontoPlanById(
        bokforderKodplan?.id ?? ''
      ),
    [bokforderKodplan?.id, konteringContext.konteringUtils]
  );

  const rows: UtanordningType__Associations_Rader[] = useMemo(
    () => formContext?.formState?.currentValues?._Associations?.Rader ?? [],
    [formContext?.formState?.currentValues?._Associations?.Rader]
  );

  const kodstrangar: IKodstrang[] = useMemo(
    () =>
      rows.reduce<IKodstrang[]>((prev, row) => {
        const potentialKodstrang = createKodstrang(
          row,
          enhancedBokforderKodplan?.kontodelDefs ?? []
        );

        if (potentialKodstrang !== null) {
          return [...prev, potentialKodstrang];
        } else {
          return prev;
        }
      }, []),
    [enhancedBokforderKodplan?.kontodelDefs, rows]
  );

  const currentKontrollattestant = useMemo(
    () =>
      allKontrollattestanter.find(
        ka =>
          ka.userID ===
          formContext?.formState.currentValues?._Associations?.Kontrollattestant
            ?.id
      ),
    [
      allKontrollattestanter,
      formContext?.formState.currentValues?._Associations?.Kontrollattestant
        ?.id,
    ]
  );

  const prevKontrollattestant = useMemo(
    () =>
      allKontrollattestanter.find(
        ka =>
          ka.userID === formContext?.data?._Associations?.Kontrollattestant?.id
      ),
    [
      allKontrollattestanter,
      formContext?.data?._Associations?.Kontrollattestant?.id,
    ]
  );

  const reserverForCurrentKontrollattestant = useMemo(
    () =>
      franvaroContext.franvaroReservForData.filter(franvaro => {
        return findFranvaroForUser(
          currentKontrollattestant?.userID,
          TEDEKBokforderTYPEnum.UTANORDNING,
          new Date(),
          franvaro
        );
      }),
    [currentKontrollattestant?.userID, franvaroContext.franvaroReservForData]
  );

  const reserverForPreviousKontrollattestant = useMemo(
    () =>
      franvaroContext.franvaroReservForData.filter(franvaro => {
        return findFranvaroForUser(
          prevKontrollattestant?.userID,
          TEDEKBokforderTYPEnum.UTANORDNING,
          new Date(),
          franvaro
        );
      }),
    [franvaroContext.franvaroReservForData, prevKontrollattestant?.userID]
  );

  const isCurrentUserReservForKontrollattestant = useMemo(
    () =>
      reserverForCurrentKontrollattestant.some(
        franvaro => franvaro._Associations?.Reserv?.id === userID
      ),
    [reserverForCurrentKontrollattestant, userID]
  );

  const isCurrentUserReservForPreviousKontrollattestant = useMemo(
    () =>
      reserverForPreviousKontrollattestant.some(
        franvaro => franvaro._Associations?.Reserv?.id === userID
      ),
    [reserverForPreviousKontrollattestant, userID]
  );

  const reserveNamesForPickedKontrollattestant = useMemo(
    () =>
      getReservNamesBasedOnFranvaroList(
        formContext?.data?._Associations?.Kontrollattestant?._Associations
          ?.Franvaro_All ?? reserverForCurrentKontrollattestant,
        TEDEKBokforderTYPEnum.UTANORDNING
      ),
    [
      formContext?.data?._Associations?.Kontrollattestant?._Associations
        ?.Franvaro_All,
      reserverForCurrentKontrollattestant,
    ]
  );

  const options = useMemo(
    () =>
      (allKontrollattestanter ?? []).filter(kontrollattestant =>
        filterValidKontrollattestantOption(
          formContext?.formState?.currentValues?._Associations?.Bestallare?.id,
          kontrollattestant,
          kodstrangar,
          franvaroContext.franvaroReservForData,
          franvaroContext.franvaroReservForQueryLoading,
          userID,
          formContext?.formState?.currentValues?.U_BELOPP,
          rows,
          formContext?.formState?.currentValues?.STATUS
        )
      ),
    [
      allKontrollattestanter,
      formContext?.formState?.currentValues?.STATUS,
      formContext?.formState?.currentValues?.U_BELOPP,
      formContext?.formState?.currentValues?._Associations?.Bestallare?.id,
      franvaroContext.franvaroReservForData,
      franvaroContext.franvaroReservForQueryLoading,
      kodstrangar,
      rows,
      userID,
    ]
  );

  const onChangeAttestant = useCallback(
    (
      option: UserBokforderKodbehorigheterQuery_userBokforderKodbehorigheter | null
    ) => {
      const newKontrollattestantValue: FormUpdateAssociatonType[] = [
        {
          associationName: 'Kontrollattestant',
          operation: 'Ref',
          id: option?.userID ?? null,
          values: option
            ? {
                id: option?.userID,
                Fullname: option?.namn,
              }
            : null,
        },
      ];
      formContextUpdate?.(newKontrollattestantValue);
    },
    [formContextUpdate]
  );

  const attestAndReservText = formContext?.formState.currentValues
    ?.KONTROLLATTESTERAD_DATUM
    ? formContext?.formState.currentValues?._Associations
        ?.ReservKontrollattestant != null
      ? ` (Attesterad ${formContext?.formState.currentValues?.KONTROLLATTESTERAD_DATUM} av reserv ${formContext?.formState.currentValues?._Associations?.ReservKontrollattestant.Fullname})`
      : ` (Attesterad ${formContext?.formState.currentValues?.KONTROLLATTESTERAD_DATUM})`
    : reserveNamesForPickedKontrollattestant.length > 0
    ? `(Reserv: ${reserveNamesForPickedKontrollattestant.join(', ')})`
    : '';

  const attestantText = formContext?.formState.currentValues?._Associations
    ?.Kontrollattestant?.Fullname
    ? `${
        formContext?.formState.currentValues?._Associations?.Kontrollattestant
          ?.Fullname ?? ''
      } ${attestAndReservText}`
    : null;

  const editmode = useMemo(
    () =>
      !formContext?.formState.loading &&
      !franvaroContext.franvaroReservForQueryLoading &&
      (bokforderContext.isEditMode ||
        ((formContext?.formState.currentValues?._Associations?.Kontrollattestant
          ?.id === userID ||
          formContext?.formState.currentValues?._Associations?.Kontrollattestant
            ?.id == null ||
          formContext?.data?._Associations?.Kontrollattestant?.id === userID ||
          formContext?.data?._Associations?.Kontrollattestant?.id == null ||
          isCurrentUserReservForKontrollattestant ||
          isCurrentUserReservForPreviousKontrollattestant) &&
          formContext?.data?.STATUS ===
            TEDEKBokforderSTATUSEnum.VANTAR_PA_ATTEST)),
    [
      bokforderContext.isEditMode,
      formContext?.data?.STATUS,
      formContext?.data?._Associations?.Kontrollattestant?.id,
      formContext?.formState.currentValues?._Associations?.Kontrollattestant
        ?.id,
      formContext?.formState.loading,
      franvaroContext.franvaroReservForQueryLoading,
      isCurrentUserReservForKontrollattestant,
      isCurrentUserReservForPreviousKontrollattestant,
      userID,
    ]
  );

  return (
    <EDBox component={'span'} sx={{ display: 'block' }}>
      {editmode ? (
        <EDAutoCompleteEdit<UserBokforderKodbehorigheterQuery_userBokforderKodbehorigheter>
          disabled={props.isDisabled}
          fullWidth
          getOptionLabel={option => {
            const reserver = franvaroContext.franvaroReservForData.filter(
              franvaro => {
                return findFranvaroForUser(
                  option?.userID,
                  TEDEKBokforderTYPEnum.UTANORDNING,
                  new Date(),
                  franvaro
                );
              }
            );

            const optionsReserveNames = getReservNamesBasedOnFranvaroList(
              reserver,
              TEDEKBokforderTYPEnum.UTANORDNING
            );

            const reserveNames =
              formContext?.data?._Associations?.Kontrollattestant?.id ===
              option.userID
                ? reserveNamesForPickedKontrollattestant
                : optionsReserveNames;

            const optionText =
              reserveNames.length > 0
                ? option.namn + ' (Reserv: ' + reserveNames.join(', ') + ')'
                : option.namn ?? 'Namnet kunde inte hämtas';

            return optionText;
          }}
          label="Kontrollattestant"
          options={options}
          onBlur={() =>
            formContext?.formStateDispatch({
              type: 'TOUCH',
              path: ['Kontrollattestant'],
            })
          }
          onChange={onChangeAttestant}
          value={currentKontrollattestant}
          testId="kontrollattestant-picker"
          errorMessage={
            formContext?.formState.validation?.find(
              error =>
                error.fieldName === 'Kontrollattestant' &&
                (error.type === 'Error' ||
                  (error.type === 'RequiredValueHint' &&
                    formContext?.formState?.touched?.Kontrollattestant &&
                    !error.showAsHint))
            )?.message
          }
          warningMessage={
            formContext?.formState.validation?.find(
              error =>
                error.fieldName === 'Kontrollattestant' &&
                error.type === 'Warning' &&
                !error.showAsHint
            )?.message
          }
          hintMessage={
            formContext?.formState.validation?.find(
              error =>
                error.fieldName === 'Kontrollattestant' &&
                (error.type === 'Hint' ||
                  (error.type === 'RequiredValueHint' &&
                    !formContext?.formState?.touched?.Kontrollattestant) ||
                  error.showAsHint)
            )?.message
          }
          required={
            formContext?.formState.validation?.find(
              error =>
                error.fieldName === 'Kontrollattestant' &&
                error.type === 'RequiredValueHint'
            )?.message != undefined
          }
        />
      ) : (
        <EDBox
          sx={theme => ({
            padding: `${theme.spacing(1)} ${theme.spacing(1)} 0`,
            width: '100%',
          })}
        >
          <EDCaptionedValue
            label={'Kontrollattestant'}
            name={'Kontrollattestant'}
            fillContainer
          >
            <DisplayText
              value={attestantText}
              displayer={new TEDFormatDefault().displayer}
              noValueChar="—"
            />
          </EDCaptionedValue>
        </EDBox>
      )}
    </EDBox>
  );
};

export default KontrollattestantPicker;
